import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { SearchInputHelperService } from './search-input-helper.service';
import { AutocompleteSearchResponse, AutocompleteUiItem, SearchRequest } from './search-input.model';
import { WebApiModel } from '../../models/web-api.model';
import { RouterHelperService } from '../../services/router-helper.service';
import { SettingsService } from '../../services/settings.service';
import { StateService } from '../../services/state.service';

@Injectable()
export class SearchInputService {
  displayOrder: string[];

  constructor(
    private http: HttpClient,
    private _helperService: SearchInputHelperService,
    private _stateService: StateService,
  ) {
    this.displayOrder = [
      _helperService.autocompleteItemType.HeaderSpecialties.type,
      _helperService.autocompleteItemType.Specialties.type,
      _helperService.autocompleteItemType.HeaderPeople.type,
      _helperService.autocompleteItemType.People.type,
      _helperService.autocompleteItemType.HeaderPlaces.type,
      _helperService.autocompleteItemType.Places.type,
      _helperService.autocompleteItemType.HeaderServiceCategories.type,
      _helperService.autocompleteItemType.ServiceCategories.type,
      _helperService.autocompleteItemType.HeaderServiceDescriptions.type,
      _helperService.autocompleteItemType.ServiceDescriptions.type,
    ];
  }

  /**
   *  Autocomplete items for the keywords search box, keywords will be always present in this search but location not always.
   */
  keywordLookup = (searchRequest: SearchRequest): Observable<AutocompleteSearchResponse> => {
    let serviceUrl = '';
    let queryString = '';

    if (searchRequest.location) {
      serviceUrl = SettingsService.api.autocompleteGetByKeywordAndLocationUrl;
      queryString = `${RouterHelperService.urlParam.term}=${encodeURIComponent(searchRequest.keywords)}`;
      queryString = `${queryString}&${RouterHelperService.urlParam.location}=${searchRequest.location}`;
      queryString = `${queryString}&${RouterHelperService.urlParam.latitude}=${searchRequest.latitude}`;
      queryString = `${queryString}&${RouterHelperService.urlParam.longitude}=${searchRequest.longitude}`;
    } else if (searchRequest.latitude && searchRequest.longitude) {
      serviceUrl = SettingsService.api.autocompleteGetByKeywordAndGeolocationUrl;
      queryString = `${RouterHelperService.urlParam.term}=${encodeURIComponent(searchRequest.keywords)}`;
      queryString = `${queryString}&${RouterHelperService.urlParam.latitude}=${searchRequest.latitude}`;
      queryString = `${queryString}&${RouterHelperService.urlParam.longitude}=${searchRequest.longitude}`;
    } else {
      serviceUrl = SettingsService.api.autoCompleteGetByKeywordUrl;
      queryString = `${RouterHelperService.urlParam.term}=${encodeURIComponent(searchRequest.keywords)}`;
    }

    return this.http
      .get(`${serviceUrl}?${queryString}`) // used string interpolation to replace the old RequestOptions and URLSearchParams
      .pipe(
        map((response) => {
          const results = response as WebApiModel.AutocompleteItem[];
          const orderedSpecialtiesResults = this.orderSpecialties(results);
          if (results && results[1]) {
            const autocompleteUiItem = this.buildAutocompleteItem(results[1], 1, searchRequest.keywords);
            this._stateService.saveLastTopSearchResponse(autocompleteUiItem);
          }
          return new AutocompleteSearchResponse(searchRequest, orderedSpecialtiesResults);
        }),
        catchError((error) => throwError(error)),
      );
  };

  locationLookup = (searchRequest: SearchRequest): Observable<AutocompleteSearchResponse> => {
    const serviceUrl = SettingsService.api.autocompleteLocationUrl;
    const queryString = `${RouterHelperService.urlParam.term}=${searchRequest.location}`;

    return this.http.get(`${serviceUrl}?${queryString}`).pipe(
      map((response) => {
        // Locations come back as a sting array. Re-map them into WebApiModel.AutocompleteItem
        // so both keyword and location dropdowns operate with the same autocomplete types.
        const results = response as string[];
        const autocompleteResuts = results.map((item) => {
          const autoCompleteItem = new WebApiModel.AutocompleteItem();
          autoCompleteItem.id = null;
          autoCompleteItem.name = item;
          autoCompleteItem.type = this._helperService.autocompleteItemType.Location.type;
          return autoCompleteItem;
        });
        return new AutocompleteSearchResponse(searchRequest, autocompleteResuts);
      }),
      catchError((error) => throwError(error)),
    );
  };

  // We will no longer depend on the backend to return the items in the correct order
  orderSpecialties = (results: WebApiModel.AutocompleteItem[]) =>
    results.sort((result1, result2) => {
      const orderOfResult1 = this.displayOrder.findIndex((item) => item === result1.type);
      const orderOfResult2 = this.displayOrder.findIndex((item) => item === result2.type);
      if (orderOfResult1 < orderOfResult2) {
        return -1;
      } else {
        return 1;
      }
    });

  /**
   * Create a new item to bind to the UI based on the web api data. Add extra properties to the UI item.
   * @param item - Web api AutocompleteItem
   */
  buildAutocompleteItem(item: WebApiModel.AutocompleteItem, index: number, keyword: string): AutocompleteUiItem {
    const uiItem = new AutocompleteUiItem();
    uiItem.data = item;
    uiItem.name = item.name;
    uiItem.selected = false;
    uiItem.index = index;
    uiItem.isHeader = false;
    if (item.type && item.type.startsWith('Header')) {
      uiItem.isHeader = true;
      uiItem.name = `<span class='term-label'>'${keyword}' in</span> ${item.name}`;
      if (
        item.type === this._helperService.autocompleteItemType.HeaderPlaces.type ||
        item.type === this._helperService.autocompleteItemType.HeaderPeople.type
      ) {
        uiItem.name += ` (${item.allResultsCount})`;
      }
    }

    uiItem.isProfile =
      item.type === this._helperService.autocompleteItemType.People.type ||
      item.type === this._helperService.autocompleteItemType.Places.type;

    uiItem.isClickable = item.type !== this._helperService.autocompleteItemType.NoResults.type;

    uiItem.populateInputWithName =
      item.type === this._helperService.autocompleteItemType.Location.type ||
      item.type === this._helperService.autocompleteItemType.ServiceCategories.type ||
      item.type === this._helperService.autocompleteItemType.ServiceDescriptions.type ||
      item.type === this._helperService.autocompleteItemType.Specialties.type ||
      item.type === this._helperService.autocompleteItemType.People.type ||
      item.type === this._helperService.autocompleteItemType.Places.type;

    uiItem.populateSearchTermWithName =
      item.type === this._helperService.autocompleteItemType.ServiceCategories.type ||
      item.type === this._helperService.autocompleteItemType.ServiceDescriptions.type ||
      item.type === this._helperService.autocompleteItemType.Specialties.type;

    uiItem.highlightName =
      item.type === this._helperService.autocompleteItemType.People.type ||
      item.type === this._helperService.autocompleteItemType.Places.type ||
      item.type === this._helperService.autocompleteItemType.ServiceCategories.type ||
      item.type === this._helperService.autocompleteItemType.ServiceDescriptions.type ||
      item.type === this._helperService.autocompleteItemType.Location.type ||
      item.type === this._helperService.autocompleteItemType.Specialties.type;

    uiItem.cssClass = this._helperService.getAutocompeteItemType(item.type).cssClass;

    if (item.placeLocation) {
      uiItem.subName = item.placeLocation; // Places will display name and location in the autocomplete dropdown.
    }

    if (item.type === this._helperService.autocompleteItemType.HeaderPeople.type) {
      uiItem.subName = SettingsService.autoCompleteValues.viewAllPeopleText;
    }

    if (item.type === this._helperService.autocompleteItemType.HeaderPlaces.type) {
      uiItem.subName = SettingsService.autoCompleteValues.viewAllPlacesText;
    }

    if (item.type === this._helperService.autocompleteItemType.HeaderServiceCategories.type) {
      uiItem.subName = SettingsService.autoCompleteValues.viewAllServiceCategoriesText;
    }

    if (item.type === this._helperService.autocompleteItemType.HeaderServiceDescriptions.type) {
      uiItem.subName = SettingsService.autoCompleteValues.viewAllServicesText;
    }

    if (item.type === this._helperService.autocompleteItemType.HeaderSpecialties.type) {
      uiItem.subName = SettingsService.autoCompleteValues.viewAllSpecialtiesText;
    }

    return uiItem;
  }
}
